import React, { useEffect } from 'react';
import DocsLayout from './DocsLayout';
import { Clock, CheckCircle, AlertCircle, Lightbulb } from 'lucide-react';

const QuickStart = () => {
  useEffect(() => {
    document.title = 'Quick Start Guide | TicketDesk Documentation';

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = 'Get started with TicketDesk in 60 seconds. Learn how to set up your account, connect Gmail, and create your first support ticket.';

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = 'https://www.ticketdesk.app/docs/quick-start';
  }, []);

  return (
    <DocsLayout currentPage="/docs/quick-start">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center text-sm text-gray-500 mb-4">
          <a href="/docs" className="hover:text-purple-600">Documentation</a>
          <span className="mx-2">/</span>
          <a href="/docs" className="hover:text-purple-600">Getting Started</a>
          <span className="mx-2">/</span>
          <span className="text-gray-900">Quick Start</span>
        </div>

        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
          Quick Start Guide
        </h1>

        <p className="text-lg text-gray-600">
          Get your TicketDesk account set up and ready to handle customer support in just a few minutes.
        </p>

        <div className="flex items-center gap-4 mt-4 text-sm text-gray-500">
          <div className="flex items-center">
            <Clock className="w-4 h-4 mr-1" />
            5 min read
          </div>
          <div className="flex items-center">
            <CheckCircle className="w-4 h-4 mr-1" />
            Last updated: January 2025
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="prose prose-lg max-w-none">
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg mb-8">
          <div className="flex">
            <Lightbulb className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-blue-900 mb-1">Before You Start</h4>
              <p className="text-sm text-blue-800 mb-0">
                Make sure you have access to the Gmail account you want to use for customer support. You'll need to authorize TicketDesk to read and send emails on your behalf.
              </p>
            </div>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">What You'll Learn</h2>
        <ul className="space-y-2 mb-8">
          <li>How to create your TicketDesk account</li>
          <li>How to connect your Gmail inbox</li>
          <li>How to send a test email and create your first ticket</li>
          <li>How to reply to a ticket from TicketDesk</li>
        </ul>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Step 1: Create Your Account</h2>
        <p className="text-gray-700 mb-4">
          Visit <a href="/register" className="text-purple-600 hover:text-purple-700 font-medium">ticketdesk.app/register</a> and sign up with your email address. You'll receive a verification email to confirm your account.
        </p>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 my-6">
          <p className="text-sm text-gray-600 mb-2"><strong>📸 Screenshot placeholder:</strong></p>
          <div className="bg-gray-200 h-64 rounded-lg flex items-center justify-center">
            <span className="text-gray-500">Registration page screenshot</span>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Step 2: Connect Your Gmail</h2>
        <p className="text-gray-700 mb-4">
          Once logged in, you'll be prompted to connect your Gmail account. Click "Connect Gmail" and authorize TicketDesk to access your inbox.
        </p>

        <ol className="list-decimal list-inside space-y-3 mb-6">
          <li className="text-gray-700">Click the "Connect Gmail" button</li>
          <li className="text-gray-700">Choose your Google account</li>
          <li className="text-gray-700">Review the permissions (read and send email)</li>
          <li className="text-gray-700">Click "Allow" to grant access</li>
          <li className="text-gray-700">You'll be redirected back to TicketDesk</li>
        </ol>

        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg my-6">
          <div className="flex">
            <AlertCircle className="w-5 h-5 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-yellow-900 mb-1">Important</h4>
              <p className="text-sm text-yellow-800 mb-0">
                TicketDesk will only access emails sent to your support inbox. We never access your personal emails or other Gmail data.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 my-6">
          <p className="text-sm text-gray-600 mb-2"><strong>📸 Screenshot placeholder:</strong></p>
          <div className="bg-gray-200 h-64 rounded-lg flex items-center justify-center">
            <span className="text-gray-500">Gmail OAuth connection screenshot</span>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Step 3: Send a Test Email</h2>
        <p className="text-gray-700 mb-4">
          To verify everything is working, send yourself a test email from any email address to your connected Gmail account.
        </p>

        <div className="bg-gray-100 rounded-lg p-4 font-mono text-sm mb-6">
          <div className="text-gray-600 mb-1">From: your-personal@email.com</div>
          <div className="text-gray-600 mb-1">To: support@yourbusiness.com</div>
          <div className="text-gray-600 mb-1">Subject: Test ticket</div>
          <div className="text-gray-700 mt-3">This is a test email to verify TicketDesk is working correctly.</div>
        </div>

        <p className="text-gray-700 mb-4">
          Within 1-2 minutes, you should see this email appear as a ticket in your TicketDesk dashboard.
        </p>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Step 4: Reply to Your First Ticket</h2>
        <p className="text-gray-700 mb-4">
          Click on the test ticket in your inbox, type a response, and click "Send Reply". The customer will receive your reply via email, and the conversation will be tracked in TicketDesk.
        </p>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 my-6">
          <p className="text-sm text-gray-600 mb-2"><strong>📸 Screenshot placeholder:</strong></p>
          <div className="bg-gray-200 h-64 rounded-lg flex items-center justify-center">
            <span className="text-gray-500">Ticket reply interface screenshot</span>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Next Steps</h2>
        <p className="text-gray-700 mb-4">
          Congratulations! You've successfully set up TicketDesk. Here's what to do next:
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          <a href="/docs/team-collaboration" className="block p-4 bg-white border border-gray-200 rounded-lg hover:border-purple-300 hover:shadow-sm transition-all">
            <h3 className="font-semibold text-gray-900 mb-1">Invite Your Team</h3>
            <p className="text-sm text-gray-600 mb-0">Add team members to help manage customer support</p>
          </a>

          <a href="/docs/facebook-setup" className="block p-4 bg-white border border-gray-200 rounded-lg hover:border-purple-300 hover:shadow-sm transition-all">
            <h3 className="font-semibold text-gray-900 mb-1">Connect Facebook</h3>
            <p className="text-sm text-gray-600 mb-0">Add Facebook Messenger to your unified inbox</p>
          </a>

          <a href="/docs/ai-responses" className="block p-4 bg-white border border-gray-200 rounded-lg hover:border-purple-300 hover:shadow-sm transition-all">
            <h3 className="font-semibold text-gray-900 mb-1">Use AI Polish</h3>
            <p className="text-sm text-gray-600 mb-0">Make your responses sound more professional</p>
          </a>

          <a href="/docs/review-monitoring" className="block p-4 bg-white border border-gray-200 rounded-lg hover:border-purple-300 hover:shadow-sm transition-all">
            <h3 className="font-semibold text-gray-900 mb-1">Set Up Review Alerts</h3>
            <p className="text-sm text-gray-600 mb-0">Monitor Trustpilot and Capterra reviews</p>
          </a>
        </div>

        <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 mt-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Need Help?</h3>
          <p className="text-gray-700 mb-4">
            If you run into any issues during setup, we're here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <a href="mailto:hello@ticketdesk.app" className="inline-flex items-center justify-center bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-purple-700">
              Email Support
            </a>
            <a href="/docs/troubleshooting" className="inline-flex items-center justify-center border border-purple-600 text-purple-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-purple-50">
              View Common Issues
            </a>
          </div>
        </div>
      </div>
    </DocsLayout>
  );
};

export default QuickStart;
